import { Text } from '@linktr.ee/arbor/components/Text';
import { CaretLeftIcon } from '@linktr.ee/arbor/icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthForm from '@shared/components/AuthForm';
import Button from '@shared/components/Button';
import { CookiePrefs } from '@shared/components/CookiePrefs';
import InputSelect from '@shared/components/InputSelect';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import {
  usePublishPageViewedEvent,
  usePublishScreenViewedEvent,
} from '@shared/utils/analytics/hooks';
import bannerLoginImage from '@universal-login/images/banner-login-desktop.webp';
import type {
  PhoneCountryCode,
  PhoneLoginScreenProps,
} from '@universal-login/types/descope';

const isUsNationalNumber = (value: string) =>
  /^[2-9]\d{2}[2-9]\d{6}$/.test(value);

type PhoneCountryConfig = {
  label: string;
  dialCode: string;
  placeholder: string;
  normaliseNationalNumber: (digits: string) => string | undefined;
};

const normaliseNanpNationalNumber = (digits: string): string | undefined => {
  const nationalNumber =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  return isUsNationalNumber(nationalNumber) ? nationalNumber : undefined;
};

const phoneCountries: Record<PhoneCountryCode, PhoneCountryConfig> = {
  US: {
    label: 'United States',
    dialCode: '1',
    placeholder: '415 555 2671',
    normaliseNationalNumber: normaliseNanpNationalNumber,
  },
  CA: {
    label: 'Canada',
    dialCode: '1',
    placeholder: '416 555 2671',
    normaliseNationalNumber: normaliseNanpNationalNumber,
  },
  AU: {
    label: 'Australia',
    dialCode: '61',
    placeholder: '0491 570 006',
    normaliseNationalNumber: (digits: string) => {
      const nationalNumber = digits.startsWith('61')
        ? digits.slice(2)
        : digits.startsWith('0')
          ? digits.slice(1)
          : digits;

      return /^[2-478]\d{8}$/.test(nationalNumber) ? nationalNumber : undefined;
    },
  },
  UA: {
    label: 'Ukraine',
    dialCode: '380',
    placeholder: '050 123 4567',
    normaliseNationalNumber: (digits: string) => {
      const nationalNumber = digits.startsWith('380')
        ? digits.slice(3)
        : digits.startsWith('0')
          ? digits.slice(1)
          : digits;

      return /^[1-9]\d{8}$/.test(nationalNumber) ? nationalNumber : undefined;
    },
  },
  GB: {
    label: 'United Kingdom',
    dialCode: '44',
    placeholder: '07700 900123',
    normaliseNationalNumber: (digits: string) => {
      const nationalNumber = digits.startsWith('44')
        ? digits.slice(2)
        : digits.startsWith('0')
          ? digits.slice(1)
          : digits;

      return /^[1-9]\d{8,9}$/.test(nationalNumber) ? nationalNumber : undefined;
    },
  },
};

const defaultAllowedCountries: PhoneCountryCode[] = ['US'];

const normaliseExplicitInternationalNumber = (
  digits: string,
  allowedCountries: PhoneCountryCode[]
): string | undefined => {
  const matchingCountries = [...allowedCountries].sort(
    (firstCountry, secondCountry) =>
      phoneCountries[secondCountry].dialCode.length -
      phoneCountries[firstCountry].dialCode.length
  );

  for (const country of matchingCountries) {
    const { dialCode, normaliseNationalNumber } = phoneCountries[country];
    if (!digits.startsWith(dialCode)) {
      continue;
    }

    const nationalNumber = normaliseNationalNumber(
      digits.slice(dialCode.length)
    );
    if (nationalNumber) {
      return `+${dialCode}${nationalNumber}`;
    }
  }

  return undefined;
};

const normalisePhoneNumber = (
  inputValue: string,
  country: PhoneCountryCode,
  allowedCountries: PhoneCountryCode[]
): string | undefined => {
  const trimmedValue = inputValue.trim();
  const digits = trimmedValue.replace(/\D/g, '');

  if (!digits) {
    return undefined;
  }

  if (trimmedValue.startsWith('+')) {
    return normaliseExplicitInternationalNumber(digits, allowedCountries);
  }

  const countryConfig = phoneCountries[country];
  const nationalNumber = countryConfig.normaliseNationalNumber(digits);

  return nationalNumber
    ? `+${countryConfig.dialCode}${nationalNumber}`
    : undefined;
};

const PhoneLoginScreenViewEvents: React.FC<{
  authFlow: PhoneLoginScreenProps['authFlow'];
}> = ({ authFlow }) => {
  usePublishPageViewedEvent();
  usePublishScreenViewedEvent(authFlow === 'signup' ? 'Sign Up' : 'Log In');

  return null;
};

const PhoneLoginScreen: React.FC<PhoneLoginScreenProps> = ({
  authFlow,
  onClickContinue,
  onBackButtonClick,
  errorText,
  context,
  resetError,
  allowedCountries = defaultAllowedCountries,
  defaultCountry = allowedCountries[0] ?? 'US',
  heading,
  subHeading,
  submitLabel,
  validationErrorText,
  variant = 'banner',
  showCookiePrefs = true,
  publishViewEvents = true,
}) => {
  const { t } = useTranslation();

  const initialPhone = context?.form?.phone;
  const [phoneInput, setPhoneInput] = useState(
    typeof initialPhone === 'string' ? initialPhone : ''
  );
  const [selectedCountry, setSelectedCountry] =
    useState<PhoneCountryCode>(defaultCountry);
  const [hasPhoneValidationError, setHasPhoneValidationError] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const resolvedAllowedCountries = useMemo(
    () =>
      allowedCountries.length > 0 ? allowedCountries : defaultAllowedCountries,
    [allowedCountries]
  );
  const countryOptions = useMemo(
    () =>
      resolvedAllowedCountries.map(country => ({
        text: `${phoneCountries[country].label} (+${phoneCountries[country].dialCode})`,
        value: country,
      })),
    [resolvedAllowedCountries]
  );

  const normalisedPhone = useMemo(
    () =>
      normalisePhoneNumber(
        phoneInput,
        selectedCountry,
        resolvedAllowedCountries
      ),
    [phoneInput, resolvedAllowedCountries, selectedCountry]
  );

  const handlePhoneChange = (value: string) => {
    resetError();
    setHasPhoneValidationError(false);
    setSubmitError(undefined);
    setPhoneInput(value);
  };

  const handleCountryChange = (value: PhoneCountryCode) => {
    resetError();
    setHasPhoneValidationError(false);
    setSubmitError(undefined);
    setSelectedCountry(value);
  };

  const handleContinue = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!normalisedPhone) {
      setHasPhoneValidationError(true);
      return;
    }

    setIsLoading(true);
    try {
      await onClickContinue(normalisedPhone);
    } catch (error) {
      setHasPhoneValidationError(false);
      setSubmitError(
        error instanceof Error ? error.message : 'Unable to send code.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderedErrorText = hasPhoneValidationError
    ? validationErrorText ||
      (resolvedAllowedCountries.length === 1 &&
      resolvedAllowedCountries[0] === 'US'
        ? t('Please enter a valid US phone number.')
        : t('Please enter a valid phone number.'))
    : errorText || submitError;
  const layoutProps =
    variant === 'banner'
      ? ({
          variant: 'banner',
          bannerImageDesktop: bannerLoginImage,
        } as const)
      : ({ variant: 'default' } as const);

  return (
    <Layout
      heading={
        <>
          {onBackButtonClick && (
            <div className="relative w-20 mb-3 sm:left-28">
              <Button
                onClick={async () => {
                  await onBackButtonClick();
                }}
                variant="ghost"
                fullWidth={false}
                icon={<CaretLeftIcon />}
                label={t('Back')}
              />
            </div>
          )}
          <span className="text-center">
            {heading || t('Continue with phone number')}
          </span>
        </>
      }
      subHeading={
        subHeading || t('Enter your phone number to receive a one-time code.')
      }
      {...layoutProps}
    >
      {publishViewEvents && <PhoneLoginScreenViewEvents authFlow={authFlow} />}
      <div className="space-y-6">
        <AuthForm>
          <div
            className={
              resolvedAllowedCountries.length > 1
                ? 'grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,160px)_1fr]'
                : undefined
            }
          >
            {resolvedAllowedCountries.length > 1 && (
              <InputSelect
                label={t('Country')}
                options={countryOptions}
                value={selectedCountry}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                  handleCountryChange(event.target.value as PhoneCountryCode);
                }}
              />
            )}
            <InputText
              label={t('Phone number')}
              type="text"
              inputMode="tel"
              autoComplete="tel"
              value={phoneInput}
              placeholder={
                resolvedAllowedCountries.length === 1
                  ? '+1'
                  : phoneCountries[selectedCountry].placeholder
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handlePhoneChange(event.target.value);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter' && !isLoading) {
                  event.preventDefault();
                  const mockEvent = {
                    preventDefault: () => event.preventDefault(),
                  } as React.MouseEvent<HTMLButtonElement>;
                  void handleContinue(mockEvent);
                }
              }}
            />
          </div>

          {renderedErrorText && (
            <Text
              as="p"
              variant="body-small-regular"
              className="text-red-600 text-center"
            >
              {renderedErrorText}
            </Text>
          )}

          <Button
            onClick={handleContinue}
            variant="primary"
            disabled={!phoneInput.trim()}
            label={submitLabel || t('Continue')}
            state={isLoading ? 'loading' : 'default'}
          />
        </AuthForm>
      </div>
      {showCookiePrefs && <CookiePrefs />}
    </Layout>
  );
};

export default PhoneLoginScreen;
